import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from './services/user/user.service';
import { delay, filter, take } from 'rxjs/operators';
import { Router, Scroll } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { PageTitlesService } from './services/page-titles.service';
import { TimeoutLogoutService } from './services/timeout-logout.service';
import { PosthogService } from './services/posthog.service';
import * as CookieConsent from 'vanilla-cookieconsent';
import { ConfigService } from './services/json-config.service';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { LOCAL_STORAGE_KEYS } from './services/local-storage.service';
import { SignalsService } from './services/signals.service';
import { TranslatedConstantsService } from './services/translated-constants.service';
import { PUBLIC_ROUTES } from './shared/constants';
import { LoginService } from './services/login.service';
import packageInfo from 'package.json';
import { LoggerService } from './services/logger/logger.service';

@Component({
  selector: 'libero-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent {
  public translationsLoaded = false;
  public viewBiometricOverlay = false;
  public viewNeedUpdateOverlay = false;
  public localDataUpdating = false;

  public get loggedConsult():boolean {
    return !!(this.userService.loggedConsult);
  }

  public get showAppContent():boolean {
    const showContent =
      !!(this.userService.loggedConsult) ||
      this.router.url.includes(PUBLIC_ROUTES.LOGIN)||
      this.router.url.includes(PUBLIC_ROUTES.PUBLIC_INSTRUMENTS) ||
      this.router.url.includes(PUBLIC_ROUTES.DIP_REDIRECT) ||
      this.router.url.includes(PUBLIC_ROUTES.COOKIE_TEXT) ||
      this.router.url.includes(PUBLIC_ROUTES.ADDITION_REDIRECT) ||
      this.router.url.includes(PUBLIC_ROUTES.CLIENT_ADDITION_REDIRECT) ||
      this.router.url.includes(PUBLIC_ROUTES.BLOG_PREVIEW);

    if(!showContent) {
      setTimeout(() => {
        // todo: refactor with cancellable effect to pprevent stuck on loading
        if(this.router.url === '/') {
          this.router.navigate(['/login']);
        }
      }, 5000);
    }

    return showContent;
  }

  public constructor (
    private translate: TranslateService,
    private userService: UserService,
    private router: Router,
    private viewportScroller: ViewportScroller,
    private pageTitlesService: PageTitlesService,
    private timeoutLogoutService: TimeoutLogoutService,
    private posthogService: PosthogService,
    private configService: ConfigService,
    private loginService: LoginService,
    private signalsService: SignalsService,
    private translatedConstantsService: TranslatedConstantsService,
    private loggerService: LoggerService,
  ) {
    this.initTranslations(translate);
    this.initRouterSubscribe();
    this.checkLocalData();
    this.initBrowserWorkarounds();
    this.updateUserData();
    this.logAppVersion();
  }

  private logAppVersion() {
    this.loggerService.logInfo(packageInfo.version);
  }

  private initBrowserWorkarounds(): void {
      // watch page on multiple browser panels
      this.initStorageKeyWatcher();
      this.timeoutLogoutService.initIdleTimeoutLogout();
  }

  private initTranslations(translate: TranslateService): void {
    const language = 'cs';

    translate.addLangs(['cs']);
    translate.setDefaultLang('cs');
    translate.use(language).pipe(
      take(1),
    ).subscribe({
        complete: () => {
          this.translatedConstantsService.initTranslatedConstants();
          this.pageTitlesService.initPageTitles();
          this.translationsLoaded = true;
          this.signalsService.setTranslationsLoaded();
          this.initCookieBar();
        },
        error: () => {
          this.translationsLoaded = true;
          this.signalsService.setTranslationsLoaded();
        },
    });
  }

  private initRouterSubscribe(): void {
    this.router.events
    .pipe(filter((e): e is Scroll => e instanceof Scroll))
    .pipe(delay(1))
    .subscribe((e) => {
      if (e.position) {
        this.viewportScroller.scrollToPosition(e.position);
      } else if (e.anchor) {
        this.viewportScroller.scrollToAnchor(e.anchor);
      } else {
        this.viewportScroller.scrollToPosition([0, 0]);
      }
    });
  }

  private initCookieBar(): void {
    CookieConsent.run({
        onConsent: (data) => {
          if (data.cookie.categories.includes(LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY)) {
            this.posthogService.init();
          }
        },
        onChange: (data) => {
          if (data.cookie.categories.includes(LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY)) {
            this.posthogService.init();
          }
        },
        revision: 1,
        disablePageInteraction: true,
        autoClearCookies: true,
        mode: 'opt-in',
        guiOptions: {
          consentModal: {
              layout: 'bar',
              position: 'bottom center',
          },
          preferencesModal: {
              layout: 'box',
              position: 'left',
          }
        },
        categories: {
          necessary: {
            readOnly: true
          },
          analytics: {
            readOnly: false
          }
        },
        language: {
          default: 'cs',
          translations: {
            cs: {
              consentModal: {
                  title: '',
                  description: this.translate.instant('cookieConsent.consentDescription1') + this.configService.getConfig(EnvironmentKeys.cookieConsentDocumentLink) + this.translate.instant('cookieConsent.consentDescription2'),
                  acceptAllBtn: this.translate.instant('cookieConsent.consentAcceptAllBtn'),
                  showPreferencesBtn: this.translate.instant('cookieConsent.consentShowPreferencesBtn'),
              },
              preferencesModal: {
                  title: this.translate.instant('cookieConsent.preferencesTitle'),
                  savePreferencesBtn: this.translate.instant('cookieConsent.preferencesSavePreferencesBtn'),
                  acceptAllBtn: this.translate.instant('cookieConsent.preferencesAcceptAllBtn'),
                  closeIconLabel: this.translate.instant('cookieConsent.preferencesCloseIconLabel'),
                  sections: [
                      {
                          title: '',
                          description: this.translate.instant('cookieConsent.expandedDescription1') + this.configService.getConfig(EnvironmentKeys.cookieConsentDocumentLink) + this.translate.instant('cookieConsent.expandedDescription2'),
                      }, {
                          title: this.translate.instant('cookieConsent.necessaryCookies'),
                          description: this.translate.instant('cookieConsent.necessaryCookiesDescription'),
                          linkedCategory: 'necessary',
                      }, {
                          title: this.translate.instant('cookieConsent.analyticsCookies'),
                          description: this.translate.instant('cookieConsent.analyticsCookiesDescription'),
                          linkedCategory: LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY,
                      },
                  ]
              },
          },
          }
        },
    });
  }

  private initStorageKeyWatcher(): void {
    window.addEventListener('storage', (data) => this.storageChanged(data));
  }

  private storageChanged(data: StorageEvent): void {
    if(!localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_LOCAL_STORAGE_KEY) && data.newValue !== data.oldValue) {
      this.userService.removeUserFromLocal();
      this.router.navigate(['/login']);
    }
  }

  /** used to refetch dynamic data on every app load */
  private updateUserData(): void {
    if(this.userService.loggedConsult) {
      this.loginService.fetchConsultData();
    }
  }

  private checkLocalData():void {
    this.userService.refreshTokenUpdating().subscribe({
      next: (flag) => {
        this.localDataUpdating = flag;
      }
    });
  }
}
